// src/modules/app-views/invoices/pages/Init/modals/FiscalBootstrapModal/Step3.jsx
// *******************************************************************************
// STEP 3 - NFE CONFIGURATION
// *******************************************************************************
// Third step: Configure and activate NFe service. User must enable NFe
// successfully to proceed to next step.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback, useEffect } from 'react';
import { Alert } from 'antd';
import { FileProtectOutlined } from '@ant-design/icons';
import NfeCard from 'modules/app-views/fiscalConfig/pages/Document/tabs/NfeTab/NfeCard';
import translateX from 'utils/translateX';
import appService from 'services/appService';
import axiosService from 'services/axiosService';
import { API } from 'constants/Api';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
// Fields required to activate NFe service
const NFE_REQUIRED_FIELDS = [
  { path: ['fiscalData', 'nfe', 'provider'], messageKey: 'inform_nfe_provider' },
  { path: ['fiscalData', 'nfe', 'environment'], messageKey: 'inform_nfe_environment' },
  { path: ['fiscalData', 'nfe', 'currentSerie'], messageKey: 'inform_nfe_serie' },
];

// *******************************************************************************
// STEP CONTENT COMPONENT
// *******************************************************************************
const Step3Content = ({ entityId, company, nfeEnabled, onNfeEnabled, form }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [localNfeEnabled, setLocalNfeEnabled] = useState(nfeEnabled);
  const [validating, setValidating] = useState(false);
  const [validationError, setValidationError] = useState(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Sync with prop
  useEffect(() => {
    setLocalNfeEnabled(nfeEnabled);
  }, [nfeEnabled]);

  // Populate form with company NFe data
  useEffect(() => {
    if (form && company) {
      const nfeData = company?.fiscalData?.nfe || {};

      form.setFieldsValue({
        fiscalData: {
          nfe: {
            provider: nfeData.provider || 'pachamama',
            environment: nfeData.environment || 'homologation',
            currentSerie: nfeData.currentSerie || 1,
            lastNumber: nfeData.lastNumber || 0,
            isEnabled: nfeData.isEnabled || false,
          },
        },
      });
    }
  }, [form, company]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleNfeToggle = useCallback(
    async checked => {
      setValidationError(null);

      // If turning OFF
      if (!checked) {
        setLocalNfeEnabled(false);
        onNfeEnabled?.(false);
        if (form) {
          form.setFieldValue(['fiscalData', 'nfe', 'isEnabled'], false);
        }
        appService.message({ flag: 's', text: 'service_deactivated', duration: 2 });
        return;
      }

      // If turning ON - validate first
      if (!entityId) {
        appService.notification({
          flag: 'error',
          title: 'error',
          description: 'entity_not_found',
        });
        return;
      }

      const msg = await appService.message({
        flag: 'loading',
        text: 'validating_nfe_data',
        duration: 0,
      });

      setValidating(true);

      // Validate required fields
      const emptyFields = [];
      NFE_REQUIRED_FIELDS.forEach(({ path, messageKey }) => {
        const value = form?.getFieldValue(path);
        const isEmpty = value === undefined || value === null || value === '';
        if (isEmpty) {
          emptyFields.push({ name: path, messageKey });
        }
      });

      if (emptyFields.length > 0) {
        const firstEmptyField = emptyFields[0];
        msg.update({
          flag: 'warning',
          text: firstEmptyField.messageKey,
          duration: 4,
        });

        const fieldsToMark = emptyFields.map(({ name }) => ({
          name,
          errors: [translateX('required')],
        }));
        form.setFields(fieldsToMark);

        setValidating(false);
        return;
      }

      // Backend validation
      try {
        const formValues = form?.getFieldsValue(true) || {};
        const nfeData = formValues?.fiscalData?.nfe || {};

        const [response] = await axiosService.post({
          url: API.FISCAL.SETUP_VALIDATE_SERVICE,
          data: {
            entityId,
            service: 'nfe',
            provider: nfeData.provider,
            fiscalData: {
              nfe: {
                environment: nfeData.environment,
                currentSerie: nfeData.currentSerie,
              },
            },
          },
          returnAsObject: true,
        });

        if (response?.isValid) {
          setLocalNfeEnabled(true);
          onNfeEnabled?.(true);
          if (form) {
            form.setFieldValue(['fiscalData', 'nfe', 'isEnabled'], true);
          }

          msg.update({
            flag: 'success',
            text: 'service_enabled',
            duration: 3,
          });
        } else {
          const errors = response?.errorList || [];
          const errorMessages = errors.map(e => translateX(e.i18nKey) || e.message).join('\n');

          msg.update({
            flag: 'error',
            text: 'nfe_configuration_not_validated',
            duration: 4,
          });

          setValidationError(errorMessages || translateX('validation_failed_unknown'));
        }
      } catch (error) {
        console.error('[Step3] NFe validation error:', error);

        const errorMessage =
          error.response?.data?.message ||
          error.response?.data?.error?.message ||
          translateX('validation_error_occurred');

        msg.update({
          flag: 'error',
          text: 'nfe_configuration_not_validated',
          duration: 4,
        });

        setValidationError(errorMessage);
      } finally {
        setValidating(false);
      }
    },
    [entityId, form, onNfeEnabled]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 9. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ paddingTop: 8 }}>
      {/* Info Alert */}
      <Alert
        type="info"
        showIcon
        icon={<FileProtectOutlined />}
        style={{ marginBottom: 24 }}
        title={translateX('configure_nfe')}
        description={translateX('configure_nfe_description')}
      />

      {/* NFe Card - Reusing from fiscalConfig */}
      <NfeCard
        nfeEnabled={localNfeEnabled}
        locked={false}
        validating={validating}
        validationError={validationError}
        onToggle={handleNfeToggle}
      />
    </div>
  );
};

// *******************************************************************************
// STEP CONFIGURATION
// *******************************************************************************
const step3 = ({
  entityId,
  company,
  nfeEnabled,
  onNfeEnabled,
  hidden = false,
  isConfirmStep = false,
}) => ({
  stepTitle: 'nfe_configuration',
  stepTooltip: 'configure_and_activate_nfe',
  hidden,
  isConfirmStep,
  content: ({ form }) => (
    <Step3Content
      entityId={entityId}
      company={company}
      nfeEnabled={nfeEnabled}
      onNfeEnabled={onNfeEnabled}
      form={form}
    />
  ),
  // Validation: NFe must be enabled to proceed (or complete if this is confirm step)
  onNext: async () => {
    if (!nfeEnabled) {
      const { default: appService } = await import('services/appService');
      appService.message({
        flag: 'warning',
        text: isConfirmStep ? 'enable_nfe_to_complete' : 'enable_nfe_to_continue',
        duration: 4,
      });
      return false; // Prevent navigation
    }
    return true; // Allow navigation
  },
});

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default step3;
